import { forwardRef, ForwardRefRenderFunction } from 'react'
import {
  SelectInfoStyled,
  LabelInfosStyled,
  LabelWrapper,
  FormControlStyled
} from './style'
import { FormHelperText, MenuItem } from '@mui/material'
import { SelectInputProps } from '@mui/material/Select/SelectInput'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { theme } from 'styles/theme/hav-theme'

export interface OptionProps {
  value: string | number
  title: string
}

export type WidthMenuProps = {
  startPosition: number | string
  endPosition: number | string
}

export interface SelectProps {
  value: string | number
  defaultValue?: string | number
  name: string
  label?: string
  height?: number
  options?: Array<OptionProps>
  onChange?: SelectInputProps<unknown>['onChange']
  error?: object | string | null
  required?: boolean
  focused?: boolean
  menuText?: string
  disabled?: boolean
  fullWidth?: boolean
  disableScrollLock?: boolean
  id?: string
  optionsMaxHeight?: string | number
  optionsMaxWidth?: WidthMenuProps
  labelAdornment?: React.ReactNode
  isMenu?: boolean
}

export const selectListStyle = {
  padding: theme.spacing.sm,
  gap: theme.spacing.sm,
  '& ul': {
    padding: 0
  },
  '& li': {
    borderRadius: theme.shapes.borderRadius.sm,
    border: `1px solid ${theme.colors.quartenary}`,
    ':not(:last-child)': {
      marginBottom: theme.spacing.xs
    },
    fontSize: theme.typography.sizes.xs
  }
}

const SelectComponent: ForwardRefRenderFunction<HTMLInputElement, SelectProps> = (
  {
    value,
    defaultValue,
    name,
    label,
    height = 30,
    onChange,
    options = [],
    error = null,
    required = false,
    focused = false,
    menuText = 'Selecionar',
    disabled,
    disableScrollLock,
    id,
    optionsMaxHeight,
    optionsMaxWidth,
    labelAdornment,
    isMenu = false,
    fullWidth = true
  },
  ref
) => {
  return (
    <FormControlStyled
      variant="outlined"
      fullWidth={fullWidth}
      {...(error && { error: true })}
    >
      {label && (
        <LabelWrapper disabled={disabled} data-testid="label-select">
          <LabelInfosStyled id="LabelInputForm" required={required} focused={focused}>
            {label}
          </LabelInfosStyled>
          {labelAdornment && <>{labelAdornment}</>}
        </LabelWrapper>
      )}
      <SelectInfoStyled
        inputRef={ref}
        data-testid="select"
        name={name}
        displayEmpty
        value={value}
        defaultValue={defaultValue}
        fullWidth={fullWidth}
        IconComponent={ExpandMoreIcon}
        onChange={onChange}
        disabled={disabled}
        style={{ height: height }}
        inputProps={{ id: id }}
        MenuProps={{
          disableScrollLock: disableScrollLock,
          PaperProps: {
            sx: {
              ...selectListStyle,
              width: optionsMaxWidth?.endPosition,
              left: `${optionsMaxWidth?.startPosition}px !important`
            }
          },
          style: {
            maxHeight: optionsMaxHeight ? optionsMaxHeight : '100vh'
          }
        }}
        sx={{
          fontFamily: isMenu
            ? theme.typography.family.medium
            : theme.typography.family.primary,
          color: isMenu ? theme.colors.primary.main : theme.colors.neutral.black
        }}
      >
        {!value && (
          <MenuItem disabled value="">
            <em>{menuText}</em>
          </MenuItem>
        )}
        {options.map((option, key) => (
          <MenuItem key={key} value={option.value}>
            <span>{option.title}</span>
          </MenuItem>
        ))}
      </SelectInfoStyled>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControlStyled>
  )
}

export const Select = forwardRef(SelectComponent)
